import React, { useState, useEffect, useRef } from 'react'
import {
  Modal,
  Input,
  Icon,
  message,
  Slider,
  Tooltip,
  Button,
  Upload,
  Progress,
  Spin
} from 'antd'
import { uploadCustomVoice } from '../../../../../services/api/aiVideoGeneration'

const { TextArea } = Input

const AUDIO_PREVIEW_BASE_URL = 'https://video-generate.sharelookapp.link/audio/'

const VoiceoverModal = ({
  visible,
  scene,
  voices = [],
  loadingVoices,
  selectedVoice: globalSelectedVoice, // Pre-selected voice from Voiceover step
  onClose,
  onGenerate,
  onRefreshVoices,
  onNavigateToStep,
  isClusteringAudio,
  audioClusteringProgress = 0,
  audioClusteringStatus = ''
}) => {
  const [voiceText, setVoiceText] = useState('')
  // Use global selected voice if available, otherwise allow local selection
  const [localSelectedVoice, setLocalSelectedVoice] = useState(null)
  const [speedFactor, setSpeedFactor] = useState(0.7)
  const [exaggeration, setExaggeration] = useState(0.5)
  const [cfgWeight, setCfgWeight] = useState(0.8)
  const [isGenerating, setIsGenerating] = useState(false)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [playingVoice, setPlayingVoice] = useState(null)
  const [showUpload, setShowUpload] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const audioRef = useRef(null)

  // Determine the effective selected voice (global takes priority)
  const selectedVoice = globalSelectedVoice || localSelectedVoice
  const hasGlobalVoice = Boolean(globalSelectedVoice)

  useEffect(() => {
    if (visible && scene && !isGenerating) {
      // Handle both string and object formats for aggregated_voiceover
      const voiceoverData = scene.aggregated_voiceover
      const initialText =
        typeof voiceoverData === 'string'
          ? voiceoverData
          : voiceoverData?.text || ''

      // Only set initial text if it's empty to allow user edits to persist
      if (!voiceText) {
        setVoiceText(initialText)
      }

      // Don't reset other fields on scene update
      if (!visible) {
        setLocalSelectedVoice(undefined)
        setSpeedFactor(1.0)
        setExaggeration(1.0)
        setCfgWeight(0.8)
        setShowAdvanced(false)
        setShowUpload(false)
      }
    }
  }, [visible]) // Only run when visibility changes

  useEffect(() => {
    // Stop audio when modal closes
    return () => {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current = null
      }
    }
  }, [])

  const handlePlayPreview = voiceId => {
    if (playingVoice === voiceId) {
      // Stop playing
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current = null
      }
      setPlayingVoice(null)
    } else {
      // Start playing
      if (audioRef.current) {
        audioRef.current.pause()
      }
      const audio = new Audio(`${AUDIO_PREVIEW_BASE_URL}${voiceId}`)
      audio.play().catch(err => {
        console.error('Audio preview failed:', err)
        message.error('Failed to play audio preview')
      })
      audio.onended = () => setPlayingVoice(null)
      audioRef.current = audio
      setPlayingVoice(voiceId)
    }
  }

  const handleGenerate = async () => {
    if (!selectedVoice) {
      message.warning(
        'Please select a voice in the Voiceover step or select one below'
      )
      return
    }

    if (
      !voiceText ||
      (typeof voiceText === 'string' && voiceText.trim().length === 0)
    ) {
      message.warning('Please enter voiceover text')
      return
    }

    const currentText = voiceText
    const currentVoice = selectedVoice
    const currentSpeed = speedFactor
    const currentExaggeration = exaggeration
    const currentCfg = cfgWeight

    setIsGenerating(true)

    try {
      await onGenerate({
        voice_id: currentVoice,
        text: currentText,
        speed_factor: currentSpeed,
        exaggeration: currentExaggeration,
        cfg_weight: currentCfg
      })
      message.success('Voiceover generated successfully')
      // onClose() - Prevent closing on success as per user request
    } catch (err) {
      message.error(err?.message || 'Failed to generate voiceover')
      setVoiceText(currentText)
      setLocalSelectedVoice(currentVoice)
      setSpeedFactor(currentSpeed)
      setExaggeration(currentExaggeration)
      setCfgWeight(currentCfg)
    } finally {
      setIsGenerating(false)
    }
  }

  const handleUploadVoice = async options => {
    const { file, onSuccess, onError } = options

    console.log('Upload triggered with file:', file)

    const formData = new FormData()
    formData.append('voice_file', file)
    formData.append('voice_name', file.name.replace(/\.[^/.]+$/, ''))

    console.log('FormData prepared:', {
      voice_file: file.name,
      voice_name: file.name.replace(/\.[^/.]+$/, '')
    })

    setUploading(true)
    setUploadProgress(0)

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90))
      }, 200)

      console.log('Calling uploadCustomVoice API...')
      const response = await uploadCustomVoice(formData)

      clearInterval(progressInterval)
      setUploadProgress(100)

      message.success('Voice uploaded successfully!')
      setShowUpload(false)

      // Refresh voice list
      if (onRefreshVoices) {
        await onRefreshVoices()
      }

      // Call onSuccess callback
      if (onSuccess) {
        onSuccess(response)
      }
    } catch (err) {
      console.error('Upload failed:', err)
      message.error(err?.message || 'Failed to upload voice')

      // Call onError callback
      if (onError) {
        onError(err)
      }
    } finally {
      setUploading(false)
      setUploadProgress(0)
    }
  }

  const getSelectedVoiceName = voiceId => {
    if (!voiceId) return null
    const voice = voices.find(
      v => v?.id === voiceId || v?.voice_id === voiceId || v === voiceId
    )
    return voice?.name || voice?.label || voiceId
  }

  const characterCount = voiceText?.length || 0
  const hasAudioLink = scene?.audio_link
  const isOverLimit = characterCount > 5000

  // Render Loading Overlay
  if (isClusteringAudio) {
    return (
      <Modal
        visible={visible}
        footer={null}
        width={520}
        maskClosable={false}
        onCancel={onClose}
        centered
        bodyStyle={{ padding: '48px 32px', textAlign: 'center' }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Spin size="large" />
          <h3
            style={{
              marginTop: 24,
              fontSize: 18,
              fontWeight: 600,
              color: '#262626'
            }}
          >
            Generating Audio...
          </h3>
          <p style={{ color: '#8c8c8c', marginBottom: 24, fontSize: 14 }}>
            Please wait while we regenerate the audio for all scenes.
          </p>

          <div style={{ width: '100%', maxWidth: 360, marginBottom: 8 }}>
            <Progress
              percent={audioClusteringProgress}
              status={
                audioClusteringStatus === 'failed' ? 'exception' : 'active'
              }
              strokeColor={{
                '0%': '#108ee9',
                '100%': '#87d068'
              }}
            />
          </div>

          <div
            style={{
              fontSize: 13,
              color: '#595959',
              minHeight: 20
            }}
          >
            {audioClusteringStatus === 'failed'
              ? 'Generation failed. Please try again.'
              : audioClusteringStatus || 'Processing...'}
          </div>
        </div>
      </Modal>
    )
  }

  return (
    <Modal
      visible={visible}
      onCancel={isGenerating ? null : onClose}
      footer={null}
      width={680}
      maskClosable={!isGenerating}
      destroyOnClose
      bodyStyle={{ padding: 0 }}
      centered
    >
      {/* Header */}
      <div
        style={{
          padding: '24px 32px',
          borderBottom: '1px solid #f0f0f0',
          color: 'black'
        }}
      >
        <div style={{ fontSize: 22, fontWeight: 600 }}>
          <Icon type="audio" style={{ marginRight: 12 }} />
          Generate Voiceover
        </div>
        <div style={{ fontSize: 13, opacity: 0.9, marginTop: 6 }}>
          {scene?.title?.toUpperCase() || 'Untitled Scene'}
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '32px 32px 24px' }}>
        {/* Current Audio Preview */}
        {hasAudioLink && !isGenerating && (
          <div
            style={{
              marginBottom: 28,
              padding: 16,
              background: '#f6ffed',
              borderRadius: 8,
              border: '1px solid #d9f7be'
            }}
          >
            <div
              style={{
                fontSize: 13,
                color: '#389e0d',
                fontWeight: 500,
                marginBottom: 10,
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <Icon
                type="check-circle"
                theme="filled"
                style={{ marginRight: 6 }}
              />
              Current Voiceover
              {scene.audio_duration && (
                <span
                  style={{
                    marginLeft: 'auto',
                    color: '#8c8c8c',
                    fontWeight: 400
                  }}
                >
                  {scene.audio_duration}s
                </span>
              )}
            </div>
            <audio
              controls
              src={scene.audio_link}
              style={{ width: '100%', height: 32 }}
            />
          </div>
        )}

        {/* Selected Voice Display */}
        {hasGlobalVoice && (
          <div
            style={{
              marginBottom: 24,
              padding: 16,
              background: '#f0f5ff',
              borderRadius: 8,
              border: '1px solid #d6e4ff'
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Icon type="sound" style={{ fontSize: 18, color: '#667eea' }} />
                <div>
                  <div
                    style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 2 }}
                  >
                    Using Selected Voice
                  </div>
                  <div
                    style={{ fontSize: 15, fontWeight: 500, color: '#262626' }}
                  >
                    {getSelectedVoiceName(globalSelectedVoice)}
                  </div>
                </div>
              </div>
              <Button
                type="link"
                size="small"
                icon={
                  playingVoice === globalSelectedVoice
                    ? 'pause-circle'
                    : 'play-circle'
                }
                onClick={() => handlePlayPreview(globalSelectedVoice)}
                style={{
                  fontSize: 20,
                  color:
                    playingVoice === globalSelectedVoice ? '#667eea' : '#8c8c8c'
                }}
              />
            </div>
          </div>
        )}

        {/* Warning if no voice selected */}
        {!selectedVoice && (
          <div
            style={{
              marginBottom: 24,
              padding: '16px 20px',
              background: '#fff1f0',
              border: '1px solid #ffccc7',
              borderRadius: 8,
              display: 'flex',
              flexDirection: 'column',
              gap: 12
            }}
          >
            <div style={{ display: 'flex', gap: 12 }}>
              <Icon
                type="info-circle"
                theme="filled"
                style={{ color: '#ff4d4f', fontSize: 20, marginTop: 2 }}
              />
              <div>
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 500,
                    color: '#262626',
                    marginBottom: 4
                  }}
                >
                  No Voice Selected
                </div>
                <div
                  style={{ fontSize: 13, color: '#595959', lineHeight: 1.5 }}
                >
                  You need to select a voice to generate audio for this scene.
                  Please go back to the Voiceover section to choose a voice.
                </div>
              </div>
            </div>
            <Button
              type="primary"
              ghost
              icon="arrow-left"
              onClick={() => {
                onClose() // Close modal first
                if (onNavigateToStep) {
                  onNavigateToStep('voiceover')
                }
              }}
              style={{
                alignSelf: 'flex-start',
                marginLeft: 32,
                borderColor: '#ff4d4f',
                color: '#ff4d4f'
              }}
            >
              Go to Voice Selection
            </Button>
          </div>
        )}

        {/* Script Input */}
        <div style={{ marginBottom: 20, opacity: !selectedVoice ? 0.5 : 1 }}>
          <label
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: 14,
              fontWeight: 500,
              color: '#262626',
              marginBottom: 8
            }}
          >
            <span>
              Script <span style={{ color: '#ff4d4f' }}>*</span>
            </span>
            <span
              style={{
                fontSize: 13,
                fontWeight: 400,
                color: isOverLimit ? '#ff4d4f' : '#8c8c8c'
              }}
            >
              {characterCount.toLocaleString()} / 5,000
            </span>
          </label>
          <TextArea
            rows={6}
            value={voiceText}
            onChange={e => setVoiceText(e.target.value)}
            placeholder="Enter the text you want to convert to speech..."
            disabled={isGenerating || !selectedVoice}
            style={{
              fontSize: 14,
              lineHeight: 1.6,
              borderColor: isOverLimit ? '#ff4d4f' : undefined,
              resize: 'none'
            }}
          />
          {isOverLimit && (
            <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 6 }}>
              Script exceeds maximum length
            </div>
          )}
        </div>

        {/* Advanced Settings Toggle */}
        <div
          onClick={() => {
            if (!selectedVoice || isGenerating) return
            setShowAdvanced(!showAdvanced)
          }}
          style={{
            cursor: isGenerating ? 'not-allowed' : 'pointer',
            fontSize: 13,
            color: '#667eea',
            marginBottom: showAdvanced ? 20 : 0,
            display: 'inline-flex',
            alignItems: 'center',
            opacity: isGenerating ? 0.5 : 1,
            fontWeight: 500
          }}
        >
          <Icon
            type={showAdvanced ? 'up' : 'down'}
            style={{ fontSize: 12, marginRight: 6 }}
          />
          Advanced Settings
        </div>

        {/* Advanced Settings Panel */}
        {showAdvanced && (
          <div
            style={{
              padding: '20px 20px 16px',
              background: '#fafafa',
              borderRadius: 8
            }}
          >
            {/* Speed Factor */}
            <div style={{ marginBottom: 24 }}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: 13,
                  fontWeight: 500,
                  color: '#595959',
                  marginBottom: 12
                }}
              >
                Speed Factor
                <Tooltip
                  title="Controls playback speed. 0.5 = half speed, 2.0 = double speed"
                  overlayStyle={{ maxWidth: 250 }}
                >
                  <Icon
                    type="question-circle"
                    style={{
                      marginLeft: 6,
                      color: '#bfbfbf',
                      fontSize: 13,
                      cursor: 'help'
                    }}
                  />
                </Tooltip>
              </label>
              <Slider
                value={speedFactor}
                onChange={setSpeedFactor}
                min={0.5}
                max={2.0}
                step={0.1}
                marks={{ 0.5: '0.5x', 1.0: '1.0x', 2.0: '2.0x' }}
                disabled={isGenerating}
              />
            </div>

            {/* Exaggeration */}
            <div style={{ marginBottom: 24 }}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: 13,
                  fontWeight: 500,
                  color: '#595959',
                  marginBottom: 12
                }}
              >
                Exaggeration
                <Tooltip
                  title="Controls expression intensity. Range: 0.1 to 2.0, default 1.0"
                  overlayStyle={{ maxWidth: 250 }}
                >
                  <Icon
                    type="question-circle"
                    style={{
                      marginLeft: 6,
                      color: '#bfbfbf',
                      fontSize: 13,
                      cursor: 'help'
                    }}
                  />
                </Tooltip>
              </label>
              <Slider
                value={exaggeration}
                onChange={setExaggeration}
                min={0.1}
                max={2.0}
                step={0.1}
                marks={{ 0.1: '0.1', 1.0: '1.0', 2.0: '2.0' }}
                disabled={isGenerating}
              />
            </div>

            {/* CFG Weight */}
            <div>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: 13,
                  fontWeight: 500,
                  color: '#595959',
                  marginBottom: 12
                }}
              >
                Guidance Weight (CFG)
                <Tooltip
                  title="Controls how much the audio should adhere to the script. Range: 0.0 to 1.0, default 0.8"
                  overlayStyle={{ maxWidth: 250 }}
                >
                  <Icon
                    type="question-circle"
                    style={{
                      marginLeft: 6,
                      color: '#bfbfbf',
                      fontSize: 13,
                      cursor: 'help'
                    }}
                  />
                </Tooltip>
              </label>
              <Slider
                value={cfgWeight}
                onChange={setCfgWeight}
                min={0.0}
                max={1.0}
                step={0.1}
                marks={{ 0.0: '0.0', 0.8: '0.8', 1.0: '1.0' }}
                disabled={isGenerating}
              />
            </div>
          </div>
        )}

        {/* Generating Status */}
        {isGenerating && (
          <div
            style={{
              marginTop: 20,
              padding: 14,
              background: 'linear-gradient(135deg, #e6f0ff 0%, #f0e6ff 100%)',
              borderRadius: 8,
              textAlign: 'center',
              fontSize: 13,
              color: '#667eea',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 500
            }}
          >
            <Icon
              type="loading"
              spin
              style={{ marginRight: 8, fontSize: 16 }}
            />
            Generating voiceover, this may take a moment...
          </div>
        )}
      </div>

      {/* Footer Actions */}
      <div
        style={{
          padding: '16px 32px',
          borderTop: '1px solid #f0f0f0',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 12,
          background: '#fafafa'
        }}
      >
        <Button
          size="large"
          onClick={onClose}
          disabled={isGenerating}
          style={{ minWidth: 80 }}
        >
          Cancel
        </Button>
        <Button
          type="primary"
          size="large"
          onClick={handleGenerate}
          loading={isGenerating}
          disabled={
            !selectedVoice ||
            !voiceText ||
            (typeof voiceText === 'string' && !voiceText.trim()) ||
            isOverLimit
          }
          style={{
            minWidth: 140,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            border: 'none'
          }}
        >
          {isGenerating ? 'Generating...' : 'Generate Voiceover'}
        </Button>
      </div>
    </Modal>
  )
}

export default VoiceoverModal
